import R from "ramda";
import { BOOTSTRAP_CATEGORIES } from "actions/bootstrap-actions";
import {
  CREATE_CATEGORY,
  SET_ADD_CATEGORY_VISIBILITY,
  UPDATE_NEW_CATEGORY_NAME,
  REMOVE_INCLUDE_RULE,
  SELECT_CATEGORIES,
  DESELECT_CATEGORIES,
  DELETE_CATEGORIES_LOADING,
  DELETE_CATEGORIES_LOADING_STOP,
  RELOAD_CATEGORIES,
  FOCUS_CATEGORY,
  UNFOCUS_CATEGORY,
  UPDATE_CATEGORY_NAME,
  REMOVE_DROPDOWN_RULE,
  UPDATE_INCLUDE_VENDORS,
  SET_DELETE_CATEGORIES_VISIBILITY,
  SAVE_CATEGORY_LOADING,
  SAVE_CATEGORY_LOADING_STOP,
  ORDER_CATEGORIES
} from "actions/categories-actions";

export const initialState = {
  categories: [],
  focusedCategory: null,
  isAddCategoryVisible: false,
  isDeleteCategoriesVisible: false,
  isDeleteCategoriesLoading: false,
  isSaveCategoryLoading: false,
  newCategory: null,
  newIncludeRules: [],
  orderBy: "name",
  orderDirection: "ASC",
  selectedCategoryIds: []
};

export const categoriesReducer = (state = initialState, action) => {
  switch (action.type) {
    case BOOTSTRAP_CATEGORIES:
    case RELOAD_CATEGORIES:
      return R.merge(state, { categories: action.data.categories });
    case CREATE_CATEGORY:
      return R.merge(state, { categories: R.prepend(action.data, state.categories) });
    case SET_ADD_CATEGORY_VISIBILITY:
      return R.merge(state, { isAddCategoryVisible: action.data.visibility });
    case UPDATE_NEW_CATEGORY_NAME:
      return R.merge(state, { newCategory: action.data.name });
    case REMOVE_INCLUDE_RULE:
      return stateForRemoveIncludeRule(state, action);
    case SELECT_CATEGORIES:
      return stateForSelectCategories(state, action.data.categoryIds);
    case DESELECT_CATEGORIES:
      return stateForDeselectCategories(state, action.data.categoryIds);
    case DELETE_CATEGORIES_LOADING:
      return R.merge(state, { isDeleteCategoriesLoading: true });
    case DELETE_CATEGORIES_LOADING_STOP:
      return stateForDeleteCategoriesLoadingStop(state, action);
    case FOCUS_CATEGORY:
      return stateForFocusCategory(state, action);
    case UNFOCUS_CATEGORY:
      return R.merge(state, { focusedCategory: null });
    case UPDATE_CATEGORY_NAME:
      return R.set(R.lensPath(["focusedCategory", "name"]), action.data.name, state);
    case REMOVE_DROPDOWN_RULE:
      return stateForRemoveDropdownRule(state, action.data);
    case UPDATE_INCLUDE_VENDORS:
      return stateForUpdateIncludeVendors(state, action.data.vendor);
    case SET_DELETE_CATEGORIES_VISIBILITY:
      return R.merge(state, { isDeleteCategoriesVisible: action.data.visibility });
    case SAVE_CATEGORY_LOADING:
      return R.merge(state, { isSaveCategoryLoading: true });
    case SAVE_CATEGORY_LOADING_STOP:
      return R.merge(state, { isSaveCategoryLoading: false, focusedCategory: null });
    case ORDER_CATEGORIES:
      return stateForOrderCategories(state, action);
    default:
      return state;
  }
};

const stateForRemoveIncludeRule = (state, action) => {
  const oldCategory = R.find(R.propEq("id", action.data.categoryId))(state.categories);
  const lens = R.lensPath(["rules", "includeVendors"]);
  const lensIndex = R.lensIndex(R.findIndex(R.propEq("id", action.data.categoryId))(state.categories));
  const newCategory = createNewCategory(oldCategory, lens, action.data.ruleId);
  return updateCategories(newCategory, lensIndex, state);
};

const createNewCategory = (oldCategory, lens, ruleId) => {
  const newRules = R.reject((R.propEq("id", ruleId)), (R.view(lens, oldCategory)));
  return R.set(lens, newRules, oldCategory);
};

const updateCategories = (newCategory, lensIndex, state) => {
  const newCategories = R.set(lensIndex, newCategory, state.categories);
  return R.merge(state, { categories: newCategories });
};

const stateForSelectCategories = (state, categoryIds) => {
  const categoryIdsArray = typeof categoryIds === "string" ? [categoryIds] : categoryIds;
  const currentIds = state.selectedCategoryIds;
  const newIds = categoryIdsArray.filter(id => {
    const category = R.find(R.propEq("id", id))(state.categories);
    if (category.type === "system") {
      return null;
    }
    return !currentIds.includes(id);
  });
  const appendedIds = R.append(newIds, currentIds);
  return R.merge(state, { selectedCategoryIds: R.flatten(appendedIds) });
};

const stateForDeselectCategories = (state, categoryIds) => {
  const currentIds = state.selectedCategoryIds;
  const newIds = currentIds.filter(id => !categoryIds.includes(id));
  return R.merge(state, { selectedCategoryIds: newIds });
};


const stateForUpdateIncludeVendors = (state, vendor) => {
  const lens = R.lensProp("newIncludeRules");
  return R.set(lens, R.append(vendor, R.view(lens, state)), state);
};

const stateForRemoveDropdownRule = (state, data) => {
  const lens = R.lensPath(["focusedCategory", "rules", "includeVendors"]);
  const newLens = R.lensProp("newIncludeRules");
  if (data.isNew) {
    const newRules = R.reject(R.propEq("name", data.rule.name), R.view(newLens, state));
    return R.set(newLens, newRules, state);
  }
  const newRules = R.reject(R.propEq("id", data.rule.id), R.view(lens, state));
  return R.set(lens, newRules, state);
};

const stateForDeleteCategoriesLoadingStop = (state) => (
  R.merge(state, {
    isDeleteCategoriesLoading: false,
    isDeleteCategoriesVisible: false,
    selectedCategoryIds: []
  })
);

const stateForFocusCategory = (state, action) => {
  const newCategory = R.assoc("style", action.data.style, action.data.category);
  return R.merge(state, { focusedCategory: newCategory, newIncludeRules: [] });
};

const stateForOrderCategories = (state, action) => {
  const column = action.data.column;
  switch (action.data.column) {
    case "name":
      return orderBy(state, column, (a, b) => b[column].localeCompare(a[column]));
    case "receipts":
      return orderBy(state, column, (a, b) => b[column] - a[column]);
    case "rules":
      return orderBy(state, column, (a, b) => b[column].includeVendors.length - a[column].includeVendors.length);
    default:
      return state.categories;
  }
};

const orderBy = (state, column, sortFunction) => {
  if (state.orderDirection === "ASC") {
    return R.merge(state, {
      orderBy: column,
      orderDirection: "DSC",
      categories: R.sort(sortFunction, state.categories)
    });
  }
  return R.merge(state, {
    orderBy: column,
    orderDirection: "ASC",
    categories: R.reverse(R.sort(sortFunction, state.categories))
  });
};



// WEBPACK FOOTER //
// ./src/js/reducers/categories-reducer.js